import { inject, Injectable, signal } from '@angular/core';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { IConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import {
  AuthConfig,
  WebFrontendConfig,
} from '@app/finxone-web-frontend/app/lib/services/config-service/config.type';
import {
  ActiveConfig,
  AlertStyleConfigurationType,
  AlternativeSystemSystemConfig,
  APP_ZONES,
  BaseCssConfig,
  baseCssConfigStyle,
  BaseCssProperties,
  BaseWidgetProperties,
  BorderRadiusClass,
  ComponentLabel,
  defaultCssProperties,
  defaultGridPosition,
  DeviceData,
  FileUploadResponse,
  getPrimaryTheme,
  IFrameAdditionalConfig,
  IFrameAdditionalConfigStyle,
  LineAdditionalConfigStyle,
  NavBarDesktop,
  PageNavigationMetaData,
  PasswordFieldConfigurations,
  PaymentProvider,
  State,
  SystemConfiguration,
  SystemDefaultCanvasConfig,
  SystemRoles,
  SystemZoneDetail,
  TextAlignToJustifyContent,
  ThemeConfig,
  TTLMap,
  UiPageConfig,
  UiPagePageConfiguration,
  UiZoneWidgetAttributeConfig,
  ZoneConfiguration,
  hydrateBaseCssConfig,
} from '@finxone-platform/shared/sys-config-types';

import { HttpClient } from '@angular/common/http';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { removeSpaceFromString } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { removeSpaceFromRole } from '@finxone-platform/shared/workflow-mapping';
import { Store } from '@ngxs/store';
import isSvg from 'is-svg';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { ActiveConfigService } from '../../../../libs/services/active-config/active-config.service';
import { ApiService } from '../../../../libs/services/api-service/api.service';
import { RouteService } from '../../../../libs/services/route-service/route.service';
import { SystemConfigService } from '../../../../libs/services/system-config/system-config.service';
import { ProjectsState } from '../../../../libs/state/project.state';
import {
  CountryCodeForState,
  StateService,
} from '@app/finxone-web-frontend/app/lib/services/state-service/state.service';

function setupCustomerLogoToMatRegistry(
  registry: MatIconRegistry,
  domSanitizer: DomSanitizer,
  config: ThemeConfig,
) {
  let logoSvg = '<svg xmlns="http://www.w3.org/2000/svg"></svg>';
  try {
    logoSvg = window.atob(config.logo);
  } catch (error) {
    console.warn('No valid svg logo set, setting empty');
  }

  // Create a Blob URL from the SVG string
  const blob = new Blob([logoSvg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  if (isSvg(logoSvg)) registry.addSvgIcon('customer-logo', domSanitizer.bypassSecurityTrustResourceUrl(url));
}

/**
 * Not made injectable so we don't accidently inject this service into main workflow angular app
 */
@Injectable({
  providedIn: 'root',
})
export class MockConfigService implements IConfigService {
  resetWidgetConfigAlert: Subject<{ isReset: boolean; uniqueId: string }> = new Subject();
  isResetWidgetConfig: Subject<{ isReset: boolean; uniqueId: string }> = new Subject();
  activeStateConfig!: ActiveConfig;

  projectPaymentProviderObs = new BehaviorSubject<PaymentProvider>(PaymentProvider.MODULR);
  private _countries$ = new ReplaySubject<Country[]>(1);
  private _currencies$ = new BehaviorSubject<Currency[]>([]);

  private http = inject(HttpClient);
  private supportedCurrencies = ['GBP', 'EUR', 'USD'];
  public globalCssVariables = signal<Record<string, string>>({});
  public globalCssString = signal<string>('');

  constructor(
    private systemConfigService: SystemConfigService,
    private activeConfigService: ActiveConfigService,
    private apiService: ApiService,
    private registry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private store: Store,
    private routeService: RouteService,
    private stateService: StateService,
  ) {
    combineLatest([this.systemConfigService.getConfig(), this.activeConfigService.getConfig()]).subscribe(
      ([config, activeConfig]) => {
        this.activeStateConfig = activeConfig;
        this.getRoleTheme(config).subscribe((roleTheme) => {
          const theme = roleTheme.theme;
          setupCustomerLogoToMatRegistry(this.registry, this.domSanitizer, theme);
        });
      },
    );
    this.activeConfigService.getConfig().subscribe((activeConfig) => {
      this.activeStateConfig = activeConfig;

      // update configured theme if it's changed
      this.getRoles()
        .pipe(take(1))
        .subscribe((roleConfigs) => {
          const alternativeTheme = getPrimaryTheme(
            roleConfigs[formatNameAsUrl(activeConfig.activeRole)]?.alternativeThemes,
          );
          if (alternativeTheme && localStorage.getItem('configuredTheme') !== alternativeTheme) {
            localStorage.setItem('configuredTheme', alternativeTheme);

            // trigger a change on the system config to cause observables to get updated about theme
            this.systemConfigService.updateConfig((config) => config);
          }
        });
    });
    this.refreshCurrencies();
    this.refreshCountries();
  }

  getPageConfigByUniqueId(uniqueId: string): Observable<UiPageConfig | undefined> {
    return this.systemConfigService.getConfig().pipe(
      map((systemConfig: SystemConfiguration) => {
        for (const zoneKey of Object.keys(systemConfig.ui.zones)) {
          const zone = systemConfig.ui.zones[zoneKey];

          for (const roleKey of Object.keys(zone.roles)) {
            const subscriptions = zone.roles[roleKey];

            for (const subscriptionKey of Object.keys(subscriptions)) {
              const subscriptionConfig = subscriptions[subscriptionKey];

              for (const deviceKey of Object.keys(subscriptionConfig)) {
                const key = deviceKey as keyof SystemDefaultCanvasConfig;
                const deviceConfig = subscriptionConfig[key];
                if (deviceConfig) {
                  const page = deviceConfig.pages.find((p) => p.pageId === uniqueId);
                  if (page) {
                    return page;
                  }
                }
              }
            }
          }
        }
        return undefined;
      }),
      catchError((err) => {
        console.error(err);
        return of(undefined);
      }),
    );
  }

  getPageNavigationMetadata(pageId: string): PageNavigationMetaData | undefined {
    let pageNavigationMetadata = undefined;
    this.systemConfigService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const device = this.activeStateConfig.activeDevice;
        const pageMetadata = config?.pageNavigationMap?.[device]?.[pageId];
        pageNavigationMetadata = pageMetadata;
      });
    return pageNavigationMetadata;
  }

  public getRoles(): Observable<SystemRoles> {
    return this.systemConfigService.getConfig().pipe(
      map((config) => {
        return config.roles;
      }),
    );
  }

  public getAlertSettingsAndStyleConfig(): Observable<AlertStyleConfigurationType | undefined> {
    return this.systemConfigService.getConfig().pipe(
      map((config) => {
        return config.ui.alertsConfig;
      }),
    );
  }

  refreshCountries() {
    if (notInJestTest()) {
      this.http
        .get<Country[]>('./assets/JSON/countries.json')
        .pipe(
          tap((countries) => {
            this._countries$.next(countries);
          }),
          catchError((err) => {
            console.error(err);
            return throwError(() => err);
          }),
          shareReplay(1),
        )
        .subscribe();
    }
  }

  refreshCurrencies(): void {
    if (notInJestTest()) {
      this.http
        .get<Currency[]>('./assets/JSON/countries-currencies.json')
        .pipe(
          tap((currencies) => {
            this._currencies$.next(currencies);
          }),
          catchError((err) => {
            console.error(err);
            return throwError(() => err);
          }),
          shareReplay(1),
        )
        .subscribe();
    }
  }

  refreshSystemConfig(): void {
    throw new Error('Method not implemented.');
  }

  refreshConfig(): void {
    throw new Error('Method not implemented.');
  }

  getSVGFileFromURL(svgUrl: string) {
    return this.http.get(svgUrl, {
      responseType: 'text',
    });
  }

  getProfileZone(): Observable<UiPageConfig> {
    return this.systemConfigService
      .getConfig()
      .pipe(
        map(
          (v) =>
            v.ui.zones[APP_ZONES.PROFILE]?.roles[removeSpaceFromRole(this.activeStateConfig.activeRole)]?.[
              this.activeStateConfig.activeCanvasSubscription
            ]?.[this.activeStateConfig.activeDevice].pages[this.activeStateConfig.activePageIndex],
        ),
      );
  }

  public getNavBarDesktopConfig(): Observable<NavBarDesktop> {
    return this.systemConfigService.getConfig().pipe(
      map((systemConfig: SystemConfiguration) => {
        return systemConfig.ui.navbar[removeSpaceFromRole(this.activeStateConfig.activeRole)].navBarDesktop;
      }),
      catchError((err) => {
        console.error(err);
        return of();
      }),
    );
  }

  public getUpdatedSystemConfig(): Observable<SystemConfiguration> {
    return this.systemConfigService.getConfig().pipe(
      map((systemConfig: SystemConfiguration) => {
        return systemConfig;
      }),
      catchError((err) => {
        console.error(err);
        return of();
      }),
    );
  }

  private getRoleTheme(systemConfig: SystemConfiguration): Observable<AlternativeSystemSystemConfig> {
    return this.activeConfigService.getConfig().pipe(
      take(1),
      map((result) => {
        const roleSlug = formatNameAsUrl(result.activeRole);
        // TODO once we support multiple themes on one persona this needs to change
        const roleTheme = systemConfig.roles[roleSlug]?.alternativeThemes?.find(
          (theme) => theme.isPrimary === true,
        )?.value;
        if (
          roleTheme &&
          systemConfig.ui.system.alternativeThemes &&
          systemConfig.ui.system.alternativeThemes[roleTheme]
        ) {
          return systemConfig.ui.system.alternativeThemes[roleTheme] ?? systemConfig.ui.system;
        } else {
          return systemConfig.ui.system;
        }
      }),
    );
  }

  getSystemName(): Observable<string> {
    return this.systemConfigService.getConfig().pipe(
      switchMap((systemConfig) => {
        return this.getRoleTheme(systemConfig).pipe(
          map((roleTheme) => {
            return roleTheme.systemName;
          }),
        );
      }),
    );
  }

  getLegalEntityName(): Observable<string> {
    return this.systemConfigService.getConfig().pipe(
      switchMap((systemConfig) => {
        return this.getRoleTheme(systemConfig).pipe(
          map((roleTheme) => {
            return roleTheme.legalEntityName;
          }),
        );
      }),
    );
  }

  load() {
    throw new Error('Method not implemented.');
  }

  getApi(): Observable<string> {
    return of('');
  }

  getFeatureFlags() {
    return of({ bfCacheAlertDetection: false });
  }

  getApiConfig(): Observable<Map<string, string>> {
    throw new Error('Method not implemented.');
  }

  getCountries(): Observable<Country[]> {
    return this._countries$.asObservable();
  }

  getCurrencies(): Observable<Currency[]> {
    return this._currencies$.asObservable();
  }

  getProjectPaymentProvider(): Observable<PaymentProvider> {
    return this.projectPaymentProviderObs.asObservable();
  }

  public checkZoneExist(zoneName: APP_ZONES) {
    return this.systemConfigService.getConfig().pipe(
      distinctUntilChanged(),
      map((systemConfig: SystemConfiguration) => {
        return systemConfig?.ui?.zones?.[zoneName];
      }),
      catchError((err) => {
        console.error(err);
        return of();
      }),
    );
  }

  getCustomQuestionData(questionUniqueId: string) {
    return this.systemConfigService.getConfig().pipe(
      distinctUntilChanged(),
      map((systemConfig) => {
        return systemConfig.ui.customData.questions[removeSpaceFromString(questionUniqueId)];
      }),
      catchError((err) => {
        console.error(err);
        return of();
      }),
    );
  }

  getCustomDataQuestionData(questionUniqueId: string) {
    return this.systemConfigService.getConfig().pipe(
      distinctUntilChanged(),
      map((systemConfig) => {
        return systemConfig.ui.customData.data[removeSpaceFromRole(questionUniqueId)];
      }),
      catchError((err) => {
        console.error(err);
        return of();
      }),
    );
  }

  getTheme(): Observable<ThemeConfig> {
    return this.systemConfigService.getConfig().pipe(
      switchMap((systemConfig) => {
        return this.getRoleTheme(systemConfig).pipe(
          map((roleTheme) => {
            return roleTheme.theme;
          }),
        );
      }),
    );
  }

  getAuthConfig(): Observable<AuthConfig> {
    throw new Error('Method not implemented.');
  }

  getConfig(): Observable<WebFrontendConfig> {
    return of({ cloudflareTurnstileSiteKey: '1x00000000000000000000AA' } as WebFrontendConfig);
  }

  public getPasswordFieldConfigurations(): Observable<PasswordFieldConfigurations> {
    return of({
      cssConfig: {
        showPasswordIcon: 'pass-eye',
        hidePasswordIcon: 'pass-eye-hide',
        successIcon: 'alert-success',
        errorIcon: 'alert-warning',
        successIconVisible: true,
        errorIconVisible: true,
        showPasswordIconVisible: true,
      },
    });
  }

  public fetchPasswordRules(): Observable<{ policy: string }> {
    return of({
      policy:
        'length(8) and upperCase(1) and lowerCase(1) and specialChars(1) and digits(1) and maxLength(20)',
    });
  }

  getWidgetsConfig() {
    return this.systemConfigService.getConfig().pipe(map((v) => v.ui));
  }

  getCurrentRoleZoneWidgetsConfig(
    zoneOverride?: string,
    pageIndexOveride?: number,
  ): Observable<UiPageConfig> {
    return this.systemConfigService.getConfig().pipe(
      map((v) => {
        const config =
          v.ui.zones[this.activeStateConfig.activeZone]?.roles[
            removeSpaceFromRole(this.activeStateConfig.activeRole)
          ]?.[this.activeStateConfig.activeCanvasSubscription][this.activeStateConfig.activeDevice];

        return structuredClone(config?.pages[pageIndexOveride ?? this.activeStateConfig.activePageIndex]);
      }),
    );
  }

  getCurrentRoleZonePagesConfig(): Observable<UiPageConfig[]> {
    return this.systemConfigService.getConfig().pipe(
      map((v) => {
        const config =
          v.ui.zones[this.activeStateConfig.activeZone]?.roles[
            removeSpaceFromRole(this.activeStateConfig.activeRole)
          ]?.[this.activeStateConfig.activeCanvasSubscription][this.activeStateConfig.activeDevice];

        return config.pages;
      }),
    );
  }

  defaultToSignedOutIfNeeded() {}

  fetchCurrentZoneConfiguration(): Observable<ZoneConfiguration> {
    return this.systemConfigService.getConfig().pipe(
      map((v) => {
        const config =
          v.ui.zones[this.activeStateConfig.activeZone]?.roles[
            removeSpaceFromRole(this.activeStateConfig.activeRole)
          ]?.[this.activeStateConfig.activeCanvasSubscription][this.activeStateConfig.activeDevice];

        return config.zoneConfiguration;
      }),
    );
  }

  getWidgetLayout(widgetName: ComponentLabel): Observable<UiPageConfig> {
    const widgetLayout: UiPageConfig = {
      pageId: '',
      url: '',
      mainComponentName: '',
      pageTypeConfig: 'new',
      pageType: 'staticGrid',
      title: '',
      widgetLayouts: [
        {
          name: widgetName,
          uniqueId: widgetName,
          gridPosition: defaultGridPosition(),
          attributes: {
            css_config: defaultCssProperties(),
            elementsConfig: [],
            widgetProperties: { textContent: '' },
          },
        },
      ],
      pageConfiguration: {
        gridsterMargin: {
          left: 1,
          right: 1,
          top: 2,
          unit: 'em',
        },
        pageMargin: {
          left: 1,
          right: 1,
          top: 2,
          unit: 'em',
        },
        showHeader: true,
        labelsAsPlaceholder: false,
        hideNavigationMenu: false,
      },
    };
    return of(widgetLayout);
  }

  onGetBaseWidgetStyle(widgetConfiguration: any, includeBorderStyle = true): baseCssConfigStyle {
    const borderStyles = {
      'border-top-left-radius': `${widgetConfiguration?.css_config?.borderRadiusTopLeft}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-top-right-radius': `${widgetConfiguration?.css_config?.borderRadiusTopRight}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-bottom-left-radius': `${widgetConfiguration?.css_config?.borderRadiusBottomLeft}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-bottom-right-radius': `${widgetConfiguration?.css_config?.borderRadiusBottomRight}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-color': `${widgetConfiguration?.css_config?.borderColor}`,
      'border-width': `${widgetConfiguration?.css_config?.borderWidth}px`,
      'border-style': `${widgetConfiguration?.css_config?.borderStyle}`,
    };
    return {
      'margin-top': `${widgetConfiguration?.css_config?.marginTop}${widgetConfiguration?.css_config?.marginUnit}`,
      'margin-left': `${widgetConfiguration?.css_config?.marginLeft}${widgetConfiguration?.css_config?.marginUnit}`,
      'margin-right': `${widgetConfiguration?.css_config?.marginRight}${widgetConfiguration?.css_config?.marginUnit}`,
      'margin-bottom': `${widgetConfiguration?.css_config?.marginBottom}${widgetConfiguration?.css_config?.marginUnit}`,
      'padding-top': `${widgetConfiguration?.css_config?.paddingTop}${widgetConfiguration?.css_config?.paddingUnit}`,
      'padding-left': `${widgetConfiguration?.css_config?.paddingLeft}${widgetConfiguration?.css_config?.paddingUnit}`,
      'padding-right': `${widgetConfiguration?.css_config?.paddingRight}${widgetConfiguration?.css_config?.paddingUnit}`,
      'padding-bottom': `${widgetConfiguration?.css_config?.paddingBottom}${widgetConfiguration?.css_config?.paddingUnit}`,
      'font-size': `${widgetConfiguration?.css_config?.fontSize}${
        widgetConfiguration?.css_config?.fontUnit ?? 'px'
      }`,
      color: `${widgetConfiguration?.css_config?.color}`,
      'text-align': `${widgetConfiguration?.css_config?.textAlignment}`,

      'font-weight': `${widgetConfiguration?.css_config?.fontWeight}`,
      'background-color': `${widgetConfiguration?.css_config?.backGroundColor}`,
      ...(includeBorderStyle ? borderStyles : {}),
      ...this.getBoxShadow(widgetConfiguration),
    };
  }

  private getFontSize(fontSize: number | string, fontUnit: string | undefined): string {
    const deviceData = this.getDeviceData();

    if (this.activeConfigService.isAppWindowPreview) {
      return `${fontSize}${fontUnit ?? 'px'}`;
    }

    switch (fontUnit) {
      case 'vw': {
        const width = deviceData.deviceWidth;
        const size = (width * Number(fontSize)) / 100;
        return `${size}px`;
      }
      case 'vh': {
        const height = deviceData.deviceHeight;
        const size = (height * Number(fontSize)) / 100;
        return `${size}px`;
      }
      default:
        return `${fontSize}${fontUnit ?? 'px'}`;
    }
  }

  onGetLineWidgetStyle(widgetProperties: BaseWidgetProperties): LineAdditionalConfigStyle {
    return {
      line: {
        'border-bottom': `${widgetProperties?.['lineThickness']}px solid ${widgetProperties?.['color']}`,
        width: `${widgetProperties?.['lineWidth']}%`,
      },
      content: {
        'padding-top': `${widgetProperties?.['paddingTop']}px`,
        'padding-bottom': `${widgetProperties?.['paddingBottom']}px`,
        'justify-content': widgetProperties?.['contentAlignment'],
        visibility: widgetProperties?.['show'] ? 'visible' : 'hidden',
      },
    };
  }

  onGetIframeWidgetStyle(widgetConfiguration: IFrameAdditionalConfig): IFrameAdditionalConfigStyle {
    return {
      content: {
        height: widgetConfiguration.verticalHeight,
      },
      baseConfig: {
        'margin-top': `${widgetConfiguration.css_config?.marginTop}${widgetConfiguration.css_config?.marginUnit}`,
        'margin-left': `${widgetConfiguration.css_config?.marginLeft}${widgetConfiguration.css_config?.marginUnit}`,
        'margin-right': `${widgetConfiguration.css_config?.marginRight}${widgetConfiguration.css_config?.marginUnit}`,
        'margin-bottom': `${widgetConfiguration.css_config?.marginBottom}${widgetConfiguration.css_config?.marginUnit}`,
        'padding-top': `${widgetConfiguration.css_config?.paddingTop}${widgetConfiguration.css_config?.paddingUnit}`,
        'padding-left': `${widgetConfiguration.css_config?.paddingLeft}${widgetConfiguration.css_config?.paddingUnit}`,
        'padding-right': `${widgetConfiguration.css_config?.paddingRight}${widgetConfiguration.css_config?.paddingUnit}`,
        'padding-bottom': `${widgetConfiguration.css_config?.paddingBottom}${widgetConfiguration.css_config?.paddingUnit}`,
      },
    };
  }

  // 10 minute timeout
  public imageCache: TTLMap<string, Promise<FileUploadResponse>> = new TTLMap(600000);

  async getImagePath(id: string, projectId = ''): Promise<FileUploadResponse> {
    projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
    const cacheKey = `${id}-${projectId}`;

    if (!this.imageCache.get(cacheKey)) {
      this.imageCache.set(cacheKey, this.fetchImagePath(id, projectId));
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.imageCache.get(cacheKey)!;
  }

  private async fetchImagePath(id: string, projectId: string): Promise<FileUploadResponse> {
    return await firstValueFrom(
      this.apiService.getCall<FileUploadResponse>(`/file`, {
        params: {
          id: id,
          projectId: projectId,
        },
      }),
    );
  }

  dropDesktop(index: number) {
    this.activeConfigService.dropIndex.next(index);
  }

  getPageUrlPageIndex(zone: string, pageUrl: string): Observable<number | undefined> {
    let pageIndex = 0;

    return this.systemConfigService.getConfig().pipe(
      take(1),
      map((systemConfig) => {
        const config =
          systemConfig.ui.zones[this.activeStateConfig.activeZone]?.roles[
            removeSpaceFromRole(this.activeStateConfig.activeRole)
          ]?.[this.activeStateConfig.activeCanvasSubscription][this.activeStateConfig.activeDevice];
        pageIndex = config?.pages?.findIndex((page) => page.url === pageUrl);
        return pageIndex;
      }),
    );
  }

  getPageUrlPage(pageUrl: string): Observable<UiPageConfig | undefined> {
    return this.systemConfigService.getConfig().pipe(
      take(1),
      map((systemConfig) => {
        const config =
          systemConfig.ui.zones[this.activeStateConfig.activeZone]?.roles[
            removeSpaceFromRole(this.activeStateConfig.activeRole)
          ]?.[this.activeStateConfig.activeCanvasSubscription][this.activeStateConfig.activeDevice];
        return config?.pages?.find((page) => page.url === pageUrl);
      }),
    );
  }

  getPageIndexPageUrl(zone: string, pageIndex: number): string | undefined {
    let pageUrl = '';

    this.systemConfigService
      .getConfig()
      .pipe(take(1))
      .subscribe((systemConfig) => {
        const config =
          systemConfig.ui.zones[zone].roles[removeSpaceFromRole(this.activeStateConfig.activeRole)]?.[
            this.activeStateConfig.activeCanvasSubscription
          ][this.activeStateConfig.activeDevice];
        pageUrl = config?.pages[pageIndex]?.url;
      });
    return pageUrl;
  }

  getDeviceData(): DeviceData {
    let gridCols: number;
    let gridRows: number;
    let numberCols: number;

    const width = this.activeConfigService.isAppWindowPreview
      ? window.innerWidth
      : this.activeStateConfig.activeScreenWidth;
    const height = this.activeConfigService.isAppWindowPreview
      ? window.innerHeight
      : this.activeStateConfig.activeScreenHeight;

    if (this.activeStateConfig.activeDevice == 'desktop') {
      numberCols = 48;

      if (this.activeConfigService.isAppWindowPreview) {
        gridCols = width / numberCols;
      } else {
        const innerWidth = document.getElementById('gridster-zone-holder')?.clientWidth;

        gridCols = (innerWidth ?? width) / numberCols;
      }

      gridRows = 40;
    } else if (this.activeStateConfig.activeDevice == 'tablet') {
      numberCols = 20;
      gridCols = width / numberCols;
      gridRows = 40;
    } else {
      numberCols = 15;
      gridCols = width / numberCols;
      gridRows = 25;
    }

    return {
      deviceType: this.activeStateConfig.activeDevice,
      deviceWidth: width,
      deviceHeight: height,
      numberCols: numberCols,
      gridCols: gridCols,
      gridRows: gridRows,
      gridster: true,
    };
  }

  getSpecificBaseCssStyle(
    widgetConfiguration: UiZoneWidgetAttributeConfig,

    propertyName: Array<string>,
  ): baseCssConfigStyle {
    let cssProperty = {};
    // stored configs may be pruned to overrides only (pruneBaseCssConfig);
    // the leaf getters below interpolate fields unguarded, so restore defaults
    const css_config = hydrateBaseCssConfig(widgetConfiguration?.css_config) as BaseCssConfig;
    widgetConfiguration = {
      ...widgetConfiguration,
      css_config,
    };

    propertyName.forEach((cssPropertyName) => {
      switch (cssPropertyName) {
        case BaseCssProperties.MARGIN:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssMargin(widgetConfiguration),
          };
          break;

        case BaseCssProperties.PADDING:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssPadding(widgetConfiguration),
          };
          break;

        case BaseCssProperties.COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.ICON_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssIconColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.RIGHT_ICON_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssRightIconColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.LEFT_ICON_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssLeftIconColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.TEXTALIGN:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssTextAlign(widgetConfiguration),
          };
          break;

        case BaseCssProperties.BORDER:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssBorder(widgetConfiguration),
          };
          break;

        case BaseCssProperties.BORDER_V2:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseBorderCss(widgetConfiguration?.css_config),
          };
          break;

        case BaseCssProperties.COLORS_V2:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseColorsCss(widgetConfiguration?.css_config),
          };
          break;

        case BaseCssProperties.INPUT_BORDER:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssInputBorder(widgetConfiguration),
          };
          break;

        case BaseCssProperties.INPUT_IDLE_BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssInputIdleBackgroundColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.ACTIVE_BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssActiveBackgroundColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.ACTIVE_BORDER_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssActiveBorder(widgetConfiguration),
          };
          break;

        case BaseCssProperties.IN_ACTIVE_BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssInActiveBackgroundColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.IN_ACTIVE_BORDER_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssInActiveBorder(widgetConfiguration),
          };
          break;

        case BaseCssProperties.INPUT_IDLE_BORDER_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssInputIdleBorderColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.INPUT_ACTIVE_BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssInputActiveBackgroundColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.SELECTED_BORDER:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssSelectedBorder(widgetConfiguration),
          };
          break;

        case BaseCssProperties.BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssBackGroundColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.V_ALIGN_DISPLAY_BLOCK:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssVerticalAlignDisplayBlock(widgetConfiguration),
          };
          break;

        case BaseCssProperties.V_ALIGN:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssVerticalAlign(widgetConfiguration),
          };
          break;
        case BaseCssProperties.OBJECT_FIT:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssObjectFit(widgetConfiguration),
          };
          break;
        case BaseCssProperties.FONT_SIZE:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssFontSize(widgetConfiguration),
          };
          break;

        case BaseCssProperties.TEXT_BOLD:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssTextBold(widgetConfiguration),
          };
          break;
        case BaseCssProperties.TEXT_ITALIC:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssTextItalic(widgetConfiguration),
          };
          break;

        case BaseCssProperties.TEXT_UNDERLINE:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssTextUnderline(widgetConfiguration),
          };
          break;
        case BaseCssProperties.TEXT_FONT_STYLE:
          cssProperty = {
            ...cssProperty,
            ...this.getTextFontStyle(widgetConfiguration),
          };
          break;
        case BaseCssProperties.BOX_SHADOW:
          cssProperty = {
            ...cssProperty,
            ...this.getBoxShadow(widgetConfiguration),
          };
          break;
        case BaseCssProperties.TEXT_INPUT_FONT_SIZE:
          cssProperty = {
            ...cssProperty,
            ...this.getTextInputFontSize(widgetConfiguration),
          };
          break;
        case BaseCssProperties.TEXT_INPUT_BG_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getTextInputBackgroundColor(widgetConfiguration),
          };
          break;
        case BaseCssProperties.ICON_SIZE:
          cssProperty = {
            ...cssProperty,
            ...this.getIconSize(widgetConfiguration),
          };
          break;

        case BaseCssProperties.LEFT_ICON_SIZE:
          cssProperty = {
            ...cssProperty,
            ...this.getLeftIconSize(widgetConfiguration),
          };
          break;

        case BaseCssProperties.RIGHT_ICON_SIZE:
          cssProperty = {
            ...cssProperty,
            ...this.getRightIconSize(widgetConfiguration),
          };
          break;

        case BaseCssProperties.FONT_WEIGHT:
          cssProperty = {
            ...cssProperty,
            ...this.getFontWeight(widgetConfiguration),
          };
          break;

        case BaseCssProperties.DISABLE_BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssDisableBackGroundColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.DISABLE_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssDisableColor(widgetConfiguration),
          };
          break;
        case BaseCssProperties.DIMENSIONS:
          cssProperty = {
            ...cssProperty,
            ...this.getDimensions(widgetConfiguration),
          };
          break;
        default:
          break;
      }
    });
    return cssProperty;
  }

  public getFontWeight(widgetConfiguration: any) {
    return {
      'font-weight': `${widgetConfiguration?.css_config?.fontWeight}`,
    };
  }

  getBaseCssObjectFit(widgetConfiguration: any) {
    return {
      'background-size': `${
        widgetConfiguration?.css_config?.selectedobjectfit == 'fill'
          ? 'inherit'
          : widgetConfiguration?.css_config?.selectedobjectfit
      }`,
      'object-fit': `${widgetConfiguration?.css_config?.selectedobjectfit}`,
      opacity: `${widgetConfiguration?.css_config?.selectedOpacity / 100}`,
    };
  }

  private getDimensions(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      height: `${widgetConfiguration?.css_config?.height}${widgetConfiguration?.css_config?.dimensionUnit}`,
      width: `${widgetConfiguration?.css_config?.width}${widgetConfiguration?.css_config?.dimensionUnit}`,
    };
  }

  private getBaseCssFontSize(widgetConfiguration: any) {
    return {
      'font-size': this.getFontSize(
        widgetConfiguration?.css_config?.fontSize ?? 16,
        widgetConfiguration?.css_config?.fontUnit,
      ),
    };
  }

  private getBaseCssTextBold(widgetConfiguration: any) {
    if (!widgetConfiguration?.css_config?.isTextBold) {
      return {};
    }
    return {
      'font-weight': `${widgetConfiguration?.css_config?.isTextBold ? 'bold' : 'unset'}`,
    };
  }

  private getBaseCssTextItalic(widgetConfiguration: any) {
    if (!widgetConfiguration?.css_config?.isiTextItalic) {
      return {};
    }
    return {
      'font-style': `${widgetConfiguration?.css_config?.isiTextItalic ? 'italic' : 'unset'}`,
    };
  }

  private getBaseCssTextUnderline(widgetConfiguration: any) {
    if (!widgetConfiguration?.css_config?.isTextUnderline) {
      return {};
    }
    return {
      'text-decoration': `${widgetConfiguration?.css_config?.isTextUnderline ? 'underline' : 'unset'}`,
    };
  }

  private getIconSize(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      height: `${widgetConfiguration?.css_config?.iconSize}${widgetConfiguration?.css_config?.iconUnit}`,
      width: `${widgetConfiguration?.css_config?.iconSize}${widgetConfiguration?.css_config?.iconUnit}`,
    };
  }

  private getLeftIconSize(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      height: `${widgetConfiguration?.css_config?.lefticonSize}${widgetConfiguration?.css_config?.leftIconUnit}`,
      width: `${widgetConfiguration?.css_config?.lefticonSize}${widgetConfiguration?.css_config?.leftIconUnit}`,
    };
  }

  private getRightIconSize(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      height: `${widgetConfiguration?.css_config?.righticonSize}${widgetConfiguration?.css_config?.rightIconUnit}`,
      width: `${widgetConfiguration?.css_config?.righticonSize}${widgetConfiguration?.css_config?.rightIconUnit}`,
    };
  }

  getTextFontStyle(widgetConfiguration: { css_config: BaseCssConfig }) {
    if (widgetConfiguration?.css_config?.textFont?.includes('var(')) {
      return {
        'font-family': `${widgetConfiguration?.css_config?.textFont}`,
      };
    }
    return {
      'font-family': `"${widgetConfiguration?.css_config?.textFont}"`,
    };
  }

  getBoxShadow(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'box-shadow': `${widgetConfiguration.css_config?.shadowOffsetX + 'px'} ${
        widgetConfiguration.css_config?.shadowOffsetY + 'px'
      } ${widgetConfiguration.css_config?.shadowBlur + 'px'} ${
        widgetConfiguration.css_config?.shadowSpread + 'px'
      } ${widgetConfiguration.css_config?.shadowColor}`,
    };
  }

  getBaseCssMargin(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'margin-top': `${widgetConfiguration?.css_config?.marginTop}${widgetConfiguration?.css_config?.marginUnit}`,
      'margin-left': `${widgetConfiguration?.css_config?.marginLeft}${widgetConfiguration?.css_config?.marginUnit}`,
      'margin-right': `${widgetConfiguration?.css_config?.marginRight}${widgetConfiguration?.css_config?.marginUnit}`,
      'margin-bottom': `${widgetConfiguration?.css_config?.marginBottom}${widgetConfiguration?.css_config?.marginUnit}`,
    };
  }

  getPageContentStyle(pageConfiguration: UiPagePageConfiguration | undefined): baseCssConfigStyle {
    return {
      'margin-top': `${pageConfiguration?.pageMargin?.top}${pageConfiguration?.pageMargin?.unit}`,
      'margin-left': `${pageConfiguration?.pageMargin?.left}${pageConfiguration?.pageMargin?.unit}`,
      'margin-right': `${pageConfiguration?.pageMargin?.right}${pageConfiguration?.pageMargin?.unit}`,
      'margin-bottom': `${pageConfiguration?.pageMargin?.bottom}${pageConfiguration?.pageMargin?.unit}`,
    };
  }

  getBaseCssPadding(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'padding-top': `${widgetConfiguration?.css_config?.paddingTop}${widgetConfiguration?.css_config?.paddingUnit}`,
      'padding-left': `${widgetConfiguration?.css_config?.paddingLeft}${widgetConfiguration?.css_config?.paddingUnit}`,
      'padding-right': `${widgetConfiguration?.css_config?.paddingRight}${widgetConfiguration?.css_config?.paddingUnit}`,
      'padding-bottom': `${widgetConfiguration?.css_config?.paddingBottom}${widgetConfiguration?.css_config?.paddingUnit}`,
    };
  }

  getBaseCssColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      color: `${widgetConfiguration?.css_config?.color}`,
    };
  }

  getBaseCssIconColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      color: `${widgetConfiguration?.css_config?.iconColor}`,
    };
  }

  getBaseCssRightIconColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      color: `${widgetConfiguration?.css_config?.rightIconColor}`,
    };
  }

  getBaseCssLeftIconColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      color: `${widgetConfiguration?.css_config?.leftIconColor}`,
    };
  }

  getBaseCssTextAlign(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'text-align': `${widgetConfiguration?.css_config?.textAlignment}`,
    };
  }

  getBaseCssBackGroundColor(widgetConfiguration: any) {
    return {
      'background-color': `${widgetConfiguration?.css_config?.backGroundColor}`,
    };
  }

  getTextInputBackgroundColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'background-color': `${widgetConfiguration?.css_config?.textInputBackgroundColor}`,
    };
  }

  public getBaseColorsCss(css_config: BaseCssConfig) {
    return {
      color: `${css_config?.color}`,
      'border-color': `${css_config?.borderColor}`,
      'background-color': `${css_config?.backGroundColor}`,
    };
  }

  public getBaseBorderCss(css_config: BaseCssConfig) {
    const styles: { [key: string]: string } = {
      'border-top-width': `${css_config?.borderWidthTop}${css_config?.borderWidthUnit ?? 'px'}`,
      'border-right-width': `${css_config?.borderWidthRight}${css_config?.borderWidthUnit ?? 'px'}`,
      'border-bottom-width': `${css_config?.borderWidthBottom}${css_config?.borderWidthUnit ?? 'px'}`,
      'border-left-width': `${css_config?.borderWidthLeft}${css_config?.borderWidthUnit ?? 'px'}`,
      'border-style': css_config?.borderStyle ?? 'solid',
    };

    // Apply border-radius only if borderClass is 'none'
    if (css_config?.borderClass === BorderRadiusClass.NONE) {
      styles['border-top-left-radius'] = `${css_config?.borderRadiusTopLeft}${css_config?.borderRadiusUnit}`;
      styles['border-top-right-radius'] =
        `${css_config?.borderRadiusTopRight}${css_config?.borderRadiusUnit}`;
      styles['border-bottom-left-radius'] =
        `${css_config?.borderRadiusBottomLeft}${css_config?.borderRadiusUnit}`;
      styles['border-bottom-right-radius'] =
        `${css_config?.borderRadiusBottomRight}${css_config?.borderRadiusUnit}`;
    }

    return styles;
  }

  getBaseCssBorder(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'border-top-left-radius': `${widgetConfiguration?.css_config?.borderRadiusTopLeft}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-top-right-radius': `${widgetConfiguration?.css_config?.borderRadiusTopRight}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-bottom-left-radius': `${widgetConfiguration?.css_config?.borderRadiusBottomLeft}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-bottom-right-radius': `${widgetConfiguration?.css_config?.borderRadiusBottomRight}${widgetConfiguration?.css_config?.borderRadiusUnit}`,
      'border-color': `${widgetConfiguration?.css_config?.borderColor}`,
      'border-width': `${widgetConfiguration?.css_config?.borderWidth}px`,
      'border-style':
        widgetConfiguration?.css_config?.borderWidth !== undefined
          ? widgetConfiguration?.css_config?.borderStyle || 'solid'
          : 'none',
    };
  }

  getBaseCssInputIdleBackgroundColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'background-color': `${widgetConfiguration?.css_config?.inputIdleBackgroundColor}`,
    };
  }

  getBaseCssActiveBackgroundColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'background-color': `${widgetConfiguration?.css_config?.['activeBackgroundColor']}`,
    };
  }

  getBaseCssActiveBorder(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'border-color': `${widgetConfiguration?.css_config?.activeBorderColor}`,
      'border-width': `${widgetConfiguration?.css_config?.inputBorderWidth}px`,
      'border-style': `${widgetConfiguration?.css_config?.inputBorderWidth === undefined ? '' : 'solid'}`,
    };
  }

  getBaseCssInActiveBackgroundColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'background-color': `${widgetConfiguration?.css_config?.['inactiveBackgroundColor']}`,
    };
  }

  getBaseCssInActiveBorder(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'border-color': `${widgetConfiguration?.css_config?.inactiveBorderColor}`,
      'border-width': `${widgetConfiguration?.css_config?.inputBorderWidth}px`,
      'border-style': `${widgetConfiguration?.css_config?.inputBorderWidth === undefined ? '' : 'solid'}`,
    };
  }

  getBaseCssInputIdleBorderColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'border-color': `${widgetConfiguration?.css_config?.inputIdleBorderColor}`,
    };
  }

  getBaseCssInputActiveBackgroundColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'background-color': `${widgetConfiguration?.css_config?.inputActiveBackgroundColor}`,
    };
  }

  getBaseCssInputBorder(widgetConfiguration: UiZoneWidgetAttributeConfig) {
    const inputBorderRadiusUnit = widgetConfiguration?.css_config?.inputBorderRadiusUnit ?? 'px';
    const inputBorderRadiusTopLeft =
      widgetConfiguration?.css_config?.inputBorderRadiusTopLeft ??
      widgetConfiguration?.widgetProperties?.inputFieldSettings?.css_config.borderRadiusTopLeft;
    const inputBorderRadiusTopRight =
      widgetConfiguration?.css_config?.inputBorderRadiusTopRight ??
      widgetConfiguration?.widgetProperties?.inputFieldSettings?.css_config.borderRadiusTopRight;
    const inputBorderRadiusBottomLeft =
      widgetConfiguration?.css_config?.inputBorderRadiusBottomLeft ??
      widgetConfiguration?.widgetProperties?.inputFieldSettings?.css_config.borderRadiusBottomLeft;
    const inputBorderRadiusBottomRight =
      widgetConfiguration?.css_config?.inputBorderRadiusBottomRight ??
      widgetConfiguration?.widgetProperties?.inputFieldSettings?.css_config.borderRadiusBottomRight;
    const inputBorderColor =
      widgetConfiguration?.css_config?.inputBorderColor ??
      widgetConfiguration?.widgetProperties?.inputFieldSettings?.css_config.borderColor;
    const inputBorderWidth =
      widgetConfiguration?.css_config?.inputBorderWidth ??
      widgetConfiguration?.widgetProperties?.inputFieldSettings?.css_config.borderWidth;

    return {
      'border-top-left-radius': `${inputBorderRadiusTopLeft}${inputBorderRadiusUnit}`,
      'border-top-right-radius': `${inputBorderRadiusTopRight}${inputBorderRadiusUnit}`,
      'border-bottom-left-radius': `${inputBorderRadiusBottomLeft}${inputBorderRadiusUnit}`,
      'border-bottom-right-radius': `${inputBorderRadiusBottomRight}${inputBorderRadiusUnit}`,
      'border-color': `${inputBorderColor}`,
      'border-width': `${inputBorderWidth}px`,
      'border-style': `${widgetConfiguration?.css_config?.inputBorderWidth === undefined ? '' : 'solid'}`,
    };
  }

  getBaseCssSelectedBorder(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'border-color': `${widgetConfiguration?.css_config?.selectedBorderColor}`,
      'border-width': `${widgetConfiguration?.css_config?.borderWidth}px`,
      'border-style': `${
        widgetConfiguration?.css_config?.borderWidth === undefined
          ? 'solid'
          : widgetConfiguration?.css_config?.borderStyle
      }`,
    };
  }

  getBaseCssVerticalAlignDisplayBlock(widgetConfiguration: { css_config: BaseCssConfig }) {
    if (widgetConfiguration?.css_config?.noAlignment) {
      return {};
    }
    return {
      display: `block`,
      'align-content': `${widgetConfiguration?.css_config?.verticalAlignment}`,
    };
  }

  getBaseCssVerticalAlign(widgetConfiguration: { css_config: BaseCssConfig }) {
    if (widgetConfiguration?.css_config?.noAlignment) {
      return {};
    }
    const justifyContent = widgetConfiguration?.css_config?.textAlignment as TextAlignToJustifyContent;
    return {
      display: `flex`,
      'align-items': `${widgetConfiguration?.css_config?.verticalAlignment}`,
      'justify-content': `${justifyContent}`,
    };
  }

  getBaseCssTextAlignToJustifyContent(widgetConfiguration: any) {
    const justifyContent = widgetConfiguration?.css_config?.textAlignment as TextAlignToJustifyContent;
    return {
      'justify-content': `${justifyContent}`,
    };
  }

  private getBaseCssDisableBackGroundColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'background-color': `${widgetConfiguration?.css_config?.disabledBgColor}`,
    };
  }

  private getBaseCssDisableColor(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      color: `${widgetConfiguration?.css_config?.disabledTextColor}`,
    };
  }

  getActiveConfigType() {
    return { isWorkflow: true };
  }

  public getZoneWithNoRoleContext(): Observable<SystemZoneDetail | undefined> {
    return this.systemConfigService.getConfig().pipe(
      take(1),
      map((systemConfig) => {
        return systemConfig.ui.zones[this.activeStateConfig.activeZone];
      }),
    );
  }

  getTextInputFontSize(widgetConfiguration: { css_config: BaseCssConfig }) {
    return {
      'font-size': this.getFontSize(
        widgetConfiguration?.css_config?.textInputFontSize ?? 16,
        widgetConfiguration?.css_config?.textInputFontUnit,
      ),
    };
  }

  getInputFieldCssStyles(widgetConfig: BaseWidgetProperties) {
    const iconStyle = this.getSearchInputCssStyles(widgetConfig, [
      BaseCssProperties.ICON_SIZE,
      BaseCssProperties.COLOR,
    ]);

    const baseStyle = this.getSearchInputCssStyles(widgetConfig, [
      BaseCssProperties.BORDER,
      BaseCssProperties.BACKGROUND_COLOR,
    ]);
    const activeStyle = this.getSearchInputCssStyles(widgetConfig, [
      BaseCssProperties.HEIGHT_WIDTH,
      BaseCssProperties.COLOR,
    ]);

    const inactiveStyle = this.getSearchInputCssStyles(widgetConfig, [
      BaseCssProperties.DISABLED_BORDER_COLOR,
      BaseCssProperties.DISABLE_BACKGROUND_COLOR,
    ]);

    return {
      iconStyle,
      baseStyle,
      activeStyle,
      inactiveStyle,
    };
  }
  getSearchInputCssStyles(
    widgetProperties: BaseWidgetProperties,
    propertyName: string[],
  ): baseCssConfigStyle {
    let cssProperty = {};
    propertyName.forEach((cssPropertyName) => {
      switch (cssPropertyName) {
        case BaseCssProperties.BORDER:
          cssProperty = {
            ...cssProperty,
            ...this.getSearchInputBorderCss(widgetProperties),
          };
          break;

        case BaseCssProperties.BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getSearchInputBackgroundColorCss(widgetProperties),
          };
          break;

        case BaseCssProperties.DISABLE_BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getSearchInputBackgroundColorCss(widgetProperties, 'disabled'),
          };
          break;

        case BaseCssProperties.DISABLED_BORDER_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getSearchInputBorderCss(widgetProperties, 'disabled'),
          };
          break;

        case BaseCssProperties.ICON_SIZE:
          cssProperty = {
            ...cssProperty,
            ...this.getSearchInputIconSize(widgetProperties),
          };
          break;

        case BaseCssProperties.HEIGHT_WIDTH:
          cssProperty = {
            ...cssProperty,
            ...this.getSearchInputIconSize(widgetProperties, 'inputHeightWidth'),
          };
          break;
        case BaseCssProperties.COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getSearchInputTextColor(widgetProperties),
          };
          break;
        default:
          break;
      }
    });

    return cssProperty;
  }
  getSearchInputTextColor(widgetProperties: BaseWidgetProperties): baseCssConfigStyle {
    return {
      color: `${widgetProperties?.inputFieldSettings?.css_config?.inputTextColor}`,
    };
  }
  getSearchInputBorderCss(widgetProperties: BaseWidgetProperties, type?: string): baseCssConfigStyle {
    const colorType = type === 'disabled' ? 'inactiveBorderColor' : 'borderColor';
    return {
      'border-top-left-radius': `${widgetProperties?.inputFieldSettings?.css_config?.borderRadiusTopLeft}${widgetProperties?.inputFieldSettings?.css_config?.borderStyleUnit}`,
      'border-top-right-radius': `${widgetProperties?.inputFieldSettings?.css_config?.borderRadiusTopRight}${widgetProperties?.inputFieldSettings?.css_config?.borderStyleUnit}`,
      'border-bottom-left-radius': `${widgetProperties?.inputFieldSettings?.css_config?.borderRadiusBottomLeft}${widgetProperties?.inputFieldSettings?.css_config?.borderStyleUnit}`,
      'border-bottom-right-radius': `${widgetProperties?.inputFieldSettings?.css_config?.borderRadiusBottomRight}${widgetProperties?.inputFieldSettings?.css_config?.borderStyleUnit}`,
      'border-color': `${widgetProperties?.inputFieldSettings?.css_config?.[colorType]}`,
      'border-width': `${widgetProperties?.inputFieldSettings?.css_config?.borderWidth}px`,
      'border-style': `${
        widgetProperties?.inputFieldSettings?.css_config?.borderWidth === undefined ? '' : 'solid'
      }`,
    };
  }
  getSearchInputBackgroundColorCss(
    widgetProperties: BaseWidgetProperties,
    type?: string,
  ): baseCssConfigStyle {
    const colorType = type === 'disabled' ? 'inactiveBackgroundColor' : 'backgroundColor';
    return {
      'background-color': `${widgetProperties?.inputFieldSettings?.css_config?.[colorType]}`,
    };
  }
  getSearchInputIconSize(widgetProperties: BaseWidgetProperties, type?: string): baseCssConfigStyle {
    if (type === 'inputHeightWidth') {
      return {
        height: `${widgetProperties?.inputFieldSettings?.css_config?.inputHeight}px`,
        width: `${widgetProperties?.inputFieldSettings?.css_config?.inputWidth}%`,
      };
    } else {
      return {
        height: `${widgetProperties?.inputFieldSettings?.css_config?.iconSize}${widgetProperties?.inputFieldSettings?.css_config?.iconSizeUnit}`,
        width: `${widgetProperties?.inputFieldSettings?.css_config?.iconSize}${widgetProperties?.inputFieldSettings?.css_config?.iconSizeUnit}`,
      };
    }
  }

  public getSupportedCurrencies(): Observable<string[]> {
    return of(this.supportedCurrencies);
  }

  public getCardCssStyles(
    widgetConfiguration: UiZoneWidgetAttributeConfig,
    propertyName: string[],
  ): baseCssConfigStyle {
    let cssProperty = {};
    propertyName.forEach((cssPropertyName) => {
      switch (cssPropertyName) {
        case BaseCssProperties.MARGIN:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssMargin(widgetConfiguration),
          };
          break;

        case BaseCssProperties.PADDING:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssPadding(widgetConfiguration),
          };
          break;

        case BaseCssProperties.COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssColor(widgetConfiguration),
          };
          break;

        case BaseCssProperties.BORDER:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssBorder(widgetConfiguration),
          };
          break;
        case BaseCssProperties.BOX_SHADOW:
          cssProperty = {
            ...cssProperty,
            ...this.getBoxShadow(widgetConfiguration),
          };
          break;
        case BaseCssProperties.HEIGHT_WIDTH:
          cssProperty = {
            ...cssProperty,
            ...this.getHeightWidth(widgetConfiguration),
          };
          break;
        case BaseCssProperties.BACKGROUND_COLOR:
          cssProperty = {
            ...cssProperty,
            ...this.getBackground(widgetConfiguration),
          };
          break;
        case BaseCssProperties.OBJECT_FIT:
          cssProperty = {
            ...cssProperty,
            ...this.getBaseCssObjectFit(widgetConfiguration),
          };
          break;
        default:
          break;
      }
    });
    return cssProperty;
  }

  public getHeightWidth(widgetConfiguration: UiZoneWidgetAttributeConfig) {
    return {
      height: `${widgetConfiguration?.css_config?.height}${widgetConfiguration?.css_config?.heightDimensionUnit}`,
      width: `${widgetConfiguration?.css_config?.width}${widgetConfiguration?.css_config?.widthDimensionUnit}`,
    };
  }
  public getBackground(widgetConfiguration: UiZoneWidgetAttributeConfig) {
    if (widgetConfiguration?.widgetProperties?.['cardStylingOptions']?.imagePath) {
      return {
        'background-image': `${widgetConfiguration.widgetProperties['cardStylingOptions'].imagePath}`,
      };
    } else if (widgetConfiguration?.widgetProperties?.['cardStylingOptions']?.useGradient) {
      return {
        background: `${widgetConfiguration?.css_config?.backgroundGradient}`,
      };
    } else {
      return {
        background: `${widgetConfiguration?.css_config?.backGroundColor}`,
      };
    }
  }
  public getSafePadding() {
    const bottomPadding = 0;
    const topPadding = 0;
    return of({ bottomPadding, topPadding });
  }

  public filterOutOnlyPageUrlFromParam(pageUrlName: string | null): string {
    if (pageUrlName?.includes('#')) {
      const urlWithPageTransition = pageUrlName?.split('#');
      return urlWithPageTransition?.[0] ?? '';
    }
    if (!pageUrlName?.includes('#') && pageUrlName?.includes('?')) {
      const urlWithPageTransition = pageUrlName?.split('?');
      return urlWithPageTransition?.[0] ?? '';
    }
    return pageUrlName ?? '';
  }

  public getStates(state: CountryCodeForState): Observable<State[]> {
    return this.stateService.getState(state);
  }
}

function notInJestTest() {
  try {
    return process?.env?.['JEST_WORKER_ID'] === undefined;
  } catch (e) {
    return true;
  }
}
